import { Alert, Spin, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CaptiveActivationOption, CaptiveActivationOptions } from '../api/captive';
import { formatRubles, isPositiveMoney } from './format';

/**
 * THE PLAN CHOICE at activation (customer, 2026-08-16).
 *
 * An Account may be created against an activation TARIFF GROUP instead of a specific
 * tariff. Its subscriber then has no plan until he picks one here, on a phone, in the
 * walled garden, before anything works.
 *
 * # The two figures, and why BOTH are on every card
 *
 * The customer asked for them by name — «how much it is charged: both remaining amount
 * now and full price monthly» — and they answer different questions:
 *
 *   СПИСАНИЕ СЕЙЧАС  — `activation_charge`. What pressing the button debits: the
 *                      prorated remainder of the current period, priced by the engine
 *                      through the same pack it will post a moment later.
 *   ДАЛЬШЕ           — `subscription_fee`. The full price of a period, i.e. what he
 *                      will pay every month from then on.
 *
 * A screen showing only the monthly price would quote a figure he is not about to be
 * charged; one showing only the charge would hide what he is signing up to. Mid-month
 * the two differ by a lot, and that difference is exactly what a subscriber misreads.
 *
 * # ⚠ THE VERDICT IS PER PLAN, and the screen must not average it
 *
 * `can_activate` / `shortfall` are computed per option by the core's ONE gate
 * arithmetic against one money read. A cheap plan may be affordable while an expensive
 * one is not, so the card says so on the card — and the «не хватает» figure a
 * subscriber acts on is the one belonging to the plan he actually selected.
 *
 * # What a subscriber is NOT shown
 *
 * Nothing about billing models as such: «A» and «B» are our vocabulary, not his. The
 * charging cadence is stated in words on the card instead, because *when* he pays is a
 * fair question and *which enum we store* is not.
 */

/** How the plan charges, in the subscriber's words rather than ours. */
function cadenceKey(billingModel: string): string {
  switch (billingModel) {
    case 'A':
      return 'planChoice.cadenceDaily';
    case 'C':
      return 'planChoice.cadencePeriod';
    default:
      // 'B' and anything unrecognised. A monthly statement is the honest default: it is
      // what every tariff on this installation does, and an unknown model must not
      // produce an empty line where a subscriber expects to read how he pays.
      return 'planChoice.cadenceMonthly';
  }
}

/** «100 Мбит/с» from kbit/s, or null when the tariff sets no rate. */
export function formatBandwidth(kbps?: number): string | null {
  if (kbps == null || kbps <= 0) return null;
  if (kbps % 1024 === 0) return `${kbps / 1024} Мбит/с`;
  // A rate that is not a whole number of megabits is stated in the unit it was actually
  // configured in, rather than rounded to a tenth: the rounded figure is a precision
  // nobody chose, and the Personal Area's own guard bans exactly that shape.
  return `${kbps} кбит/с`;
}

export function PlanChoice({
  options,
  loading,
  selected,
  onSelect,
}: {
  options: CaptiveActivationOptions | null;
  loading: boolean;
  selected: number | null;
  onSelect: (tariffID: number) => void;
}) {
  const { t } = useTranslation();

  if (loading && options == null) {
    return (
      <div className="portal-center" style={{ margin: '16px 0' }}>
        <Spin />
      </div>
    );
  }

  // ⚠ «You must choose and there is nothing to choose from» is a REAL state and it is a
  // misconfiguration, not an empty list. Rendering it as a blank area would tell the
  // subscriber the page is broken and tell the operator nothing; naming it sends him to
  // support with something they can act on.
  if (!options || options.options.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('planChoice.noneTitle')}
        description={t(options?.group_archived ? 'planChoice.noneArchived' : 'planChoice.noneBody')}
        style={{ marginBottom: 16 }}
        role="alert"
      />
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <span className="group-label">
        {options.group_name
          ? t('planChoice.labelNamed', { group: options.group_name })
          : t('planChoice.label')}
      </span>
      <p className="portal-hint" style={{ margin: '4px 0 12px' }}>
        {t('planChoice.hint')}
      </p>
      <div role="radiogroup" aria-label={t('planChoice.label')}>
        {options.options.map((o) => (
          <PlanCard
            key={o.tariff_id}
            option={o}
            selected={selected === o.tariff_id}
            onSelect={() => onSelect(o.tariff_id)}
          />
        ))}
      </div>
    </div>
  );
}

function PlanCard({
  option,
  selected,
  onSelect,
}: {
  option: CaptiveActivationOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const speed = formatBandwidth(option.bandwidth_kbps);
  const shortfall = isPositiveMoney(option.shortfall);

  return (
    <div
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      data-testid={`plan-${option.tariff_id}`}
      className={`plan-card${selected ? ' plan-card-selected' : ''}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        // A card is a real control, so it answers the keys a control answers. Without
        // this the whole choice is mouse-only, which on the one screen a subscriber may
        // be driving with a screen reader is not a detail.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="plan-card-head">
        <span className="plan-card-name">{option.name}</span>
        {speed && <Tag color="blue">{speed}</Tag>}
      </div>

      {option.description && <p className="plan-card-desc">{option.description}</p>}

      <div className="identity-row">
        <span className="k">{t('planChoice.chargeNow')}</span>
        <span className="v money">
          {/* ⚠ «Nobody could price this plan» is NOT «0,00 ₽». Publishing a zero here
              would advertise free activation for a plan the engine cannot charge. */}
          {option.charge_known ? formatRubles(option.activation_charge) : t('planChoice.priceUnknown')}
        </span>
      </div>
      <div className="identity-row">
        <span className="k">{t(cadenceKey(option.billing_model))}</span>
        <span className="v money">{formatRubles(option.subscription_fee)}</span>
      </div>
      {shortfall && (
        <div className="identity-row">
          <span className="k">{t('planChoice.shortfall')}</span>
          <span className="v money money-alert">{formatRubles(option.shortfall)}</span>
        </div>
      )}
    </div>
  );
}
